import { modelOptions, mongoose, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import {
    DeviceTypeString,
    getZodSchemaFromClass,
    type NodeId,
    NodeIdString,
    ObjectIdString,
    validate,
} from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import { LorawanDeviceKeysFieldsWithDefaults, LorawanDeviceKeysSchemaTypes } from "../types/lorawanDeviceKeys.d.js";
import { type DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "LorawanDeviceKeys" as const;
export const LORAWAN_DEVICE_KEYS_COLLECTION_NAME = "LorawanDeviceKeys" as const;

/**
 * This is how we log the keys for each device as it is programmed. These are generated at time of device programming
 * and uploaded by the production jig. They should not be modified.
 */
@modelOptions({
    schemaOptions: {
        collection: "LorawanDeviceKeys",
        timestamps: true,
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
    },
})
@post<SchemaBase<LorawanDeviceKeys>>(...addSchemaKeyHook(SCHEMA_KEY))
export class LorawanDeviceKeys extends SchemaBase<LorawanDeviceKeys, LorawanDeviceKeysFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: NodeIdString })
    @prop({ type: () => String, required: true, unique: true })
    public node_id!: NodeId;

    @validate({ zodValidator: z.coerce.date() })
    @prop({ type: () => Date, required: true })
    public date!: Date;

    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public appEUI!: string;

    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public appKey!: string;

    @validate({ zodValidator: z.boolean(), hasDefault: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public registered: boolean = false;

    @validate({ zodValidator: DeviceTypeString })
    @prop({ type: () => String, required: true })
    public deviceType!: string;

    static getters = {} as const satisfies Record<string, (self: LorawanDeviceKeys) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<LorawanDeviceKeysSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<LorawanDeviceKeys, LorawanDeviceKeysFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

LorawanDeviceKeys.dtoValidators = getZodSchemaFromClass(
    LorawanDeviceKeys,
) as unknown as DtoValidatorTypes<LorawanDeviceKeysSchemaTypes>;
